/**
 * The recipe's scale factor ("Makes 6" / ×2), remembered per recipe, per device.
 *
 * Before this existed the factor lived in two unconnected places: `RecipeDetailPage` kept it
 * in component state (so it snapped back to 1× every time you came back from cook mode), and
 * cook mode kept its own copy in the cook session, which it preferred over whatever the
 * detail page handed it (so a change made on the detail page after a cook had started was
 * silently ignored). Owner ask 2026-09: "if I change the amount and switch between Cook and
 * Recipe, the amount must stay." This key is now the single source both screens read and
 * write.
 *
 * Per device, not Yjs, on purpose: doubling a recipe for tonight is a view choice, the same
 * as the units toggle — Faye opening the same recipe on her phone should see it as written.
 * 1× is stored as "no entry", so the common case leaves nothing behind.
 */

export const SCALE_STORAGE_PREFIX = 'awwyeah:recipes:scale:';

export type ScaleStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Same bounds as the `ScaleControls` stepper — anything outside is treated as corrupt. */
const MIN_FACTOR = 0.125;
const MAX_FACTOR = 8;

function key(recipeId: string): string {
  return `${SCALE_STORAGE_PREFIX}${recipeId}`;
}

function isValidFactor(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= MIN_FACTOR &&
    value <= MAX_FACTOR
  );
}

/** The remembered factor, or `null` when there is none (or it can't be read). */
export function readStoredScale(storage: ScaleStorage, recipeId: string): number | null {
  try {
    const raw = storage.getItem(key(recipeId));
    if (raw === null) return null;
    const value = Number(raw);
    return isValidFactor(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeStoredScale(storage: ScaleStorage, recipeId: string, factor: number): void {
  try {
    if (factor === 1 || !isValidFactor(factor)) storage.removeItem(key(recipeId));
    else storage.setItem(key(recipeId), String(factor));
  } catch {
    // Best-effort — a failed write just means the next visit starts at 1×.
  }
}

/**
 * Cook mode's starting factor. The stored preference wins: it is what the detail page last
 * showed. Then whatever the detail page handed over in router state, then an older cook
 * session's copy (sessions written before this key existed), then 1×.
 */
export function resolveCookFactor(
  stored: number | null,
  incoming: number | undefined,
  sessionFactor: number | undefined,
): number {
  if (stored !== null) return stored;
  if (isValidFactor(incoming)) return incoming;
  if (isValidFactor(sessionFactor)) return sessionFactor;
  return 1;
}
